import { System } from '../core/System';
import { SpriteComponent, RenderingType } from '../components/SpriteComponent';
import { TransformComponent } from '../components/TransformComponent';
import {
  AnimationComponent,
  AnimationState,
} from '../components/AnimationComponent';
import { Entity } from '../core/Entity';

/**
 * RenderingSystem - Generic rendering system for all games
 * Does not depend on specific game components - uses SpriteComponent.renderingType
 */
export class RenderingSystem extends System {
  private context: CanvasRenderingContext2D;
  private canvas: HTMLCanvasElement;
  private lastDebugTime?: number;
  private backgroundImage?: HTMLImageElement;
  private backgroundLoaded: boolean = false;
  private backgroundColor?: string; // New: for color backgrounds
  private backgroundType: 'image' | 'color' | 'transparent' | 'default' =
    'default';

  constructor(context: CanvasRenderingContext2D) {
    super();
    this.context = context;
    this.canvas = context.canvas;
    this.registerRequiredComponent(SpriteComponent);
    this.registerRequiredComponent(TransformComponent);
  }

  public update(_deltaTime: number): void {
    // Render background first
    this.renderBackground();

    // Get all renderable entities and sort by z-index
    const entities = this.getEntities().slice();

    // Debug: Always log for the first few frames
    const now = performance.now();
    if (!this.lastDebugTime || now - this.lastDebugTime > 1000) {
      // Log every second
      console.log(
        `RenderingSystem: Update called, ${entities.length} entities to render`
      );
      this.lastDebugTime = now;

      // Debug entity details
      if (entities.length > 0) {
        console.log(
          `RenderingSystem: First entity details:`,
          entities[0].getId()
        );
        const sprite = this.componentManager.getComponent(
          entities[0],
          SpriteComponent
        ) as SpriteComponent;
        const transform = this.componentManager.getComponent(
          entities[0],
          TransformComponent
        ) as TransformComponent;
        console.log(
          `RenderingSystem: Sprite:`,
          sprite
            ? `${sprite.color} ${sprite.size.x}x${sprite.size.y} type:${sprite.renderingType}`
            : 'null'
        );
        console.log(
          `RenderingSystem: Transform:`,
          transform
            ? `${transform.position.x}, ${transform.position.y}`
            : 'null'
        );
      }
    }

    entities.sort((a, b) => {
      const spriteA = this.componentManager.getComponent(
        a,
        SpriteComponent
      ) as SpriteComponent;
      const spriteB = this.componentManager.getComponent(
        b,
        SpriteComponent
      ) as SpriteComponent;
      return spriteA.zIndex - spriteB.zIndex;
    });

    // Render each entity
    for (const entity of entities) {
      this.renderEntity(entity);
    }
  }

  /**
   * Render a single entity based on its renderingType
   */
  private renderEntity(entity: Entity): void {
    const sprite = this.componentManager.getComponent(
      entity,
      SpriteComponent
    ) as SpriteComponent;
    const transform = this.componentManager.getComponent(
      entity,
      TransformComponent
    ) as TransformComponent;

    if (!sprite.visible || sprite.opacity <= 0) {
      return;
    }

    this.context.save();

    // Apply transformations
    this.context.translate(transform.position.x, transform.position.y);
    this.context.rotate(transform.rotation + sprite.rotation); // Combine transform and sprite rotation
    this.context.scale(transform.scale.x, transform.scale.y);

    // Apply opacity
    this.context.globalAlpha = sprite.opacity;

    // Check if entity has animation component for texture rendering
    const animation = this.componentManager.getComponent(
      entity,
      AnimationComponent
    ) as AnimationComponent;

    if (animation && sprite.texture) {
      this.renderAnimatedSprite(sprite, animation, entity);
    } else {
      // Use generic rendering based on renderingType
      this.renderByType(sprite);
    }

    this.context.restore();
  }

  /**
   * Generic rendering based on sprite renderingType
   */
  private renderByType(sprite: SpriteComponent): void {
    const width = sprite.size.x;
    const height = sprite.size.y;

    switch (sprite.renderingType) {
      case RenderingType.Rectangle:
        this.renderRectangle(sprite, width, height);
        break;
      case RenderingType.RigidMudBrickWall:
        this.renderRigidMudBrickWall(sprite, width, height);
        break;
      case RenderingType.IcyWall:
        this.renderIcyWall(sprite, width, height);
        break;
      case RenderingType.SaharaWall:
        this.renderSaharaWall(sprite, width, height);
        break;
      case RenderingType.AmazonWall:
        this.renderAmazonWall(sprite, width, height);
        break;
      case RenderingType.AtlanticWall:
        this.renderAtlanticWall(sprite, width, height);
        break;
      case RenderingType.SpaceWall:
        this.renderSpaceWall(sprite, width, height);
        break;
      case RenderingType.MuddyWall:
        this.renderMuddyWall(sprite, width, height);
        break;
      case RenderingType.DesertDecoration:
        this.renderDesertDecoration(sprite, width, height);
        break;
      case RenderingType.Circle:
        this.renderCircle(sprite, width, height);
        break;
      case RenderingType.Pacman:
        this.renderPacman(sprite, width, height);
        break;
      case RenderingType.TopDownPerson:
        this.renderTopDownPerson(sprite, width, height);
        break;
      case RenderingType.Ghost:
        this.renderGhost(sprite, width, height);
        break;
      case RenderingType.PowerPellet:
        this.renderPowerPellet(sprite, width, height);
        break;
      case RenderingType.CustomShape:
        this.renderCustomShape(sprite);
        break;
      default:
        this.renderRectangle(sprite, width, height);
    }
  }

  /**
   * Render a rectangle with physics-based effects for mud brick walls
   */
  private renderRectangle(
    sprite: SpriteComponent,
    width: number,
    height: number
  ): void {
    // Check if this is a mud brick wall (brown color)
    if (sprite.color === '#8B4513') {
      this.renderMudBrickWall(sprite, width, height);
    } else {
      // Regular rectangle rendering
      this.context.fillStyle = sprite.color;
      this.context.fillRect(-width / 2, -height / 2, width, height);
    }
  }

  /**
   * Render mud brick wall with simple physics-based texture (exactly like pacman-Physics)
   */

  private renderMudBrickWall(
    sprite: SpriteComponent,
    width: number,
    height: number
  ): void {
    this.context.save();

    // Semi-transparent mud wall base
    this.context.globalAlpha = 0.2;
    this.context.fillStyle = '#8B4513'; // Saddle brown
    this.context.fillRect(-width / 2, -height / 2, width, height);

    // Add subtle border for definition
    this.context.globalAlpha = 0.6;
    this.context.strokeStyle = '#654321'; // Darker brown border
    this.context.lineWidth = 2;
    this.context.strokeRect(-width / 2, -height / 2, width, height);

    // Add mud texture particles (static positions to reduce "boiling" effect)
    for (let i = 0; i < 8; i++) {
      this.context.save();
      this.context.globalAlpha = 0.4;

      // Use seed-based positioning to make particles static
      const seed = (sprite.color.length + width + height + i) % 1000;
      const rx = -width / 2 + ((seed * 0.1) % width);
      const ry = -height / 2 + ((seed * 0.15) % height);
      const particleSize = 1 + ((seed * 0.01) % 2);

      this.context.fillStyle = '#654321'; // Dark brown particles
      this.context.beginPath();
      this.context.arc(rx, ry, particleSize, 0, Math.PI * 2);
      this.context.fill();
      this.context.restore();
    }

    // Add mud drip effects (static positions to reduce "boiling" effect)
    for (let i = 0; i < 3; i++) {
      this.context.save();
      this.context.globalAlpha = 0.3;
      const dripX = -width / 2 + (i + 1) * (width / 4);
      const dripY = -height / 2;
      const dripHeight = 3 + i * 0.5; // Static height based on index

      this.context.fillStyle = '#8B4513';
      this.context.fillRect(dripX - 1, dripY, 2, dripHeight);
      this.context.restore();
    }

    this.context.restore();
  }

  /**
   * Render muddy wall with enhanced mud brick texture and effects
   */
  private renderMuddyWall(
    sprite: SpriteComponent,
    width: number,
    height: number
  ): void {
    this.context.save();

    // Semi-transparent mud wall base
    this.context.globalAlpha = 0.8;
    this.context.fillStyle = '#8B4513'; // Saddle brown
    this.context.fillRect(-width / 2, -height / 2, width, height);

    // Add subtle border for definition
    this.context.globalAlpha = 0.6;
    this.context.strokeStyle = '#654321'; // Darker brown border
    this.context.lineWidth = 2;
    this.context.strokeRect(-width / 2, -height / 2, width, height);

    // Add mud texture particles
    for (let i = 0; i < 8; i++) {
      this.context.save();
      this.context.globalAlpha = 0.4;
      const rx = -width / 2 + Math.random() * width;
      const ry = -height / 2 + Math.random() * height;
      const particleSize = 1 + Math.random() * 2;

      this.context.fillStyle = '#654321'; // Dark brown particles
      this.context.beginPath();
      this.context.arc(rx, ry, particleSize, 0, Math.PI * 2);
      this.context.fill();
      this.context.restore();
    }

    // Add mud drip effects
    for (let i = 0; i < 3; i++) {
      this.context.save();
      this.context.globalAlpha = 0.3;
      const dripX = -width / 2 + (i + 1) * (width / 4);
      const dripY = -height / 2;
      const dripHeight = 3 + Math.random() * 4;

      this.context.fillStyle = '#654321';
      this.context.fillRect(dripX - 1, dripY, 2, dripHeight);
      this.context.restore();
    }

    this.context.restore();
  }

  /**
   * Render mud brick wall with rigid physics-based texture and effects
   */
  private renderRigidMudBrickWall(
    sprite: SpriteComponent,
    width: number,
    height: number
  ): void {
    // Base mud brick color
    this.context.fillStyle = '#8B4513';
    this.context.fillRect(-width / 2, -height / 2, width, height);

    // Add brick texture lines
    this.context.strokeStyle = '#654321'; // Darker brown for brick lines
    this.context.lineWidth = 1;

    // Horizontal brick lines
    const brickHeight = height / 4;
    for (let i = 1; i < 4; i++) {
      const y = -height / 2 + i * brickHeight;
      this.context.beginPath();
      this.context.moveTo(-width / 2, y);
      this.context.lineTo(width / 2, y);
      this.context.stroke();
    }

    // Vertical brick lines (offset every other row for realistic brick pattern)
    const brickWidth = width / 3;
    for (let row = 0; row < 4; row++) {
      const startX = row % 2 === 0 ? -width / 2 : -width / 2 + brickWidth / 2;
      const endX = row % 2 === 0 ? width / 2 : width / 2 - brickWidth / 2;

      for (let x = startX; x < endX; x += brickWidth) {
        const y = -height / 2 + row * brickHeight;
        this.context.beginPath();
        this.context.moveTo(x, y);
        this.context.lineTo(x, y + brickHeight);
        this.context.stroke();
      }
    }

    // Add shadow for depth
    this.context.fillStyle = 'rgba(0,0,0,0.2)';
    this.context.fillRect(-width / 2 + 2, -height / 2 + 2, width, height);

    // Add highlight for physics effect
    this.context.fillStyle = 'rgba(255,255,255,0.1)';
    this.context.fillRect(-width / 2, -height / 2, width, height / 3);
  }

  private renderIcyWall(
    sprite: SpriteComponent,
    width: number,
    height: number
  ): void {
    // Semi-transparent white wall with light gray borders - clean square look
    this.context.save();

    // Semi-transparent white wall base
    this.context.globalAlpha = 0.5;
    this.context.fillStyle = '#FFFFFF';
    this.context.fillRect(-width / 2, -height / 2, width, height);

    // Add subtle border for definition
    this.context.globalAlpha = 0.6;
    this.context.strokeStyle = '#D3D3D3';
    this.context.lineWidth = 2;
    this.context.strokeRect(-width / 2, -height / 2, width, height);

    // Add a few snow particles for texture
    for (let i = 0; i < 6; i++) {
      this.context.save();
      this.context.globalAlpha = 0.3;
      const rx = -width / 2 + Math.random() * width;
      const ry = -height / 2 + Math.random() * height;
      const particleSize = 1 + Math.random() * 2;
      this.context.beginPath();
      this.context.arc(rx, ry, particleSize, 0, Math.PI * 2);
      this.context.fillStyle = '#F0F8FF'; // Alice blue snow particles
      this.context.fill();
      this.context.restore();
    }

    this.context.restore();
  }

  /**
   * Render Sahara desert wall with simplified design for background
   */
  private renderSaharaWall(
    sprite: SpriteComponent,
    width: number,
    height: number
  ): void {
    // Simplified wall design that works well with the desert background
    this.context.save();

    // Semi-transparent sandstone wall with deeper color
    this.context.globalAlpha = 0.8;
    this.context.fillStyle = '#debf97'; // Deeper D2B48C sandstone color (was #debf97)
    this.context.fillRect(-width / 2, -height / 2, width, height);

    // Add subtle border for definition
    this.context.globalAlpha = 0.6;
    this.context.strokeStyle = '#A0702A'; // Darker sandy brown
    this.context.lineWidth = 2;
    this.context.strokeRect(-width / 2, -height / 2, width, height);

    // Add a few sand particles for texture
    for (let i = 0; i < 6; i++) {
      this.context.save();
      this.context.globalAlpha = 0.3;
      const rx = -width / 2 + Math.random() * width;
      const ry = -height / 2 + Math.random() * height;
      const particleSize = 1 + Math.random() * 2;
      this.context.beginPath();
      this.context.arc(rx, ry, particleSize, 0, Math.PI * 2);
      this.context.fillStyle = '#F5E6A8'; // Golden sand particles
      this.context.fill();
      this.context.restore();
    }

    this.context.restore();
  }

  /**
   * Render Amazon jungle wall with simplified design for background
   */
  private renderAmazonWall(
    sprite: SpriteComponent,
    width: number,
    height: number
  ): void {
    // Simplified wall design that works well with the jungle background
    this.context.save();

    // Semi-transparent jungle wall with deeper color
    this.context.globalAlpha = 0.8;
    this.context.fillStyle = '#556B2F'; // Dark olive green for jungle wall
    this.context.fillRect(-width / 2, -height / 2, width, height);

    // Add subtle border for definition
    this.context.globalAlpha = 0.6;
    this.context.strokeStyle = '#654321'; // Dark brown
    this.context.lineWidth = 2;
    this.context.strokeRect(-width / 2, -height / 2, width, height);

    // Add a few moss particles for texture
    for (let i = 0; i < 6; i++) {
      this.context.save();
      this.context.globalAlpha = 0.3;
      const rx = -width / 2 + Math.random() * width;
      const ry = -height / 2 + Math.random() * height;
      const particleSize = 1 + Math.random() * 2;
      this.context.beginPath();
      this.context.arc(rx, ry, particleSize, 0, Math.PI * 2);
      this.context.fillStyle = '#228B22'; // Forest green moss particles
      this.context.fill();
      this.context.restore();
    }

    this.context.restore();
  }

  /**
   * Render Atlantic ocean wall with simplified design for background
   */
  private renderAtlanticWall(
    sprite: SpriteComponent,
    width: number,
    height: number
  ): void {
    // Simplified wall design that works well with the ocean background
    this.context.save();

    // Semi-transparent ocean wall with deeper color
    this.context.globalAlpha = 0.8;
    this.context.fillStyle = '#87CEEB'; // Light sky blue for ocean wall
    this.context.fillRect(-width / 2, -height / 2, width, height);

    // Add subtle border for definition
    this.context.globalAlpha = 0.6;
    this.context.strokeStyle = '#4169E1'; // Royal blue border
    this.context.lineWidth = 2;
    this.context.strokeRect(-width / 2, -height / 2, width, height);

    // Add a few water particles for texture
    for (let i = 0; i < 6; i++) {
      this.context.save();
      this.context.globalAlpha = 0.3;
      const rx = -width / 2 + Math.random() * width;
      const ry = -height / 2 + Math.random() * height;
      const particleSize = 1 + Math.random() * 2;
      this.context.beginPath();
      this.context.arc(rx, ry, particleSize, 0, Math.PI * 2);
      this.context.fillStyle = '#B0E0E6'; // Powder blue water particles
      this.context.fill();
      this.context.restore();
    }

    this.context.restore();
  }

  /**
   * Render Space cosmic wall with simplified design for background
   */
  private renderSpaceWall(
    sprite: SpriteComponent,
    width: number,
    height: number
  ): void {
    // Simplified wall design that works well with the space background
    this.context.save();

    // Semi-transparent space wall with cosmic color
    this.context.globalAlpha = 0.5;
    this.context.fillStyle = '#12123d'; //191970 #0e5168 Midnight #12123d blue for space wall
    this.context.fillRect(-width / 2, -height / 2, width, height);

    // Add subtle border for definition
    this.context.globalAlpha = 0.2;
    this.context.strokeStyle = '#41b9e1'; //#41b9e1 Royal blue border #4169E1
    this.context.lineWidth = 2;
    this.context.strokeRect(-width / 2, -height / 2, width, height);

    // Add a few cosmic particles for texture
    for (let i = 0; i < 6; i++) {
      this.context.save();
      this.context.globalAlpha = 0.3;
      const rx = -width / 2 + Math.random() * width;
      const ry = -height / 2 + Math.random() * height;
      const particleSize = 1 + Math.random() * 2;
      this.context.beginPath();
      this.context.arc(rx, ry, particleSize, 0, Math.PI * 2);
      this.context.fillStyle = '#00BFFF'; // Deep sky blue cosmic particles
      this.context.fill();
      this.context.restore();
    }

    this.context.restore();
  }

  /**
   * Render desert decoration (emoji-based decorations)
   */
  private renderDesertDecoration(
    sprite: SpriteComponent,
    width: number,
    height: number
  ): void {
    this.context.save();

    // If there's a texture (emoji), render it
    if (sprite.texture) {
      // Set font for emoji rendering
      this.context.font = `${Math.min(width, height)}px Arial`;
      this.context.textAlign = 'center';
      this.context.textBaseline = 'middle';
      this.context.fillStyle = sprite.color;
      this.context.globalAlpha = sprite.opacity;

      // Render the emoji
      this.context.fillText(sprite.texture, 0, 0);
    } else {
      // Fallback to a simple colored circle if no texture
      this.context.fillStyle = sprite.color;
      this.context.globalAlpha = sprite.opacity;
      this.context.beginPath();
      this.context.arc(0, 0, Math.min(width, height) / 2, 0, Math.PI * 2);
      this.context.fill();
    }

    // Add a subtle glow effect for desert decorations
    this.context.save();
    this.context.globalAlpha = 0.3;
    this.context.shadowColor = sprite.color;
    this.context.shadowBlur = 8;

    if (sprite.texture) {
      this.context.fillText(sprite.texture, 0, 0);
    } else {
      this.context.beginPath();
      this.context.arc(0, 0, Math.min(width, height) / 2, 0, Math.PI * 2);
      this.context.fill();
    }

    this.context.shadowBlur = 0;
    this.context.restore();

    this.context.restore();
  }

  /**
   * Set background image for the game
   */
  public setBackgroundImage(imageSrc: string): void {
    // Reset background state
    this.backgroundImage = undefined;
    this.backgroundLoaded = false;
    this.backgroundColor = undefined;
    this.backgroundType = 'default';

    // Handle empty or null input
    if (!imageSrc || imageSrc.trim() === '') {
      console.log(
        'RenderingSystem: No background specified, using default black'
      );
      this.backgroundType = 'default';
      return;
    }

    // Handle color backgrounds (starting with #)
    if (imageSrc.startsWith('#')) {
      this.backgroundColor = imageSrc;
      this.backgroundType = 'color';
      console.log(`RenderingSystem: Color background set to ${imageSrc}`);
      return;
    }

    // Handle transparency
    if (imageSrc === 'none' || imageSrc === 'transparent') {
      this.backgroundType = 'transparent';
      console.log('RenderingSystem: Transparent background set');
      return;
    }

    // Handle special keywords
    if (imageSrc === 'desert') {
      this.createDesertBackground();
      return;
    }

    // Handle image URLs
    if (
      imageSrc.startsWith('http') ||
      imageSrc.startsWith('/') ||
      imageSrc.includes('.')
    ) {
      this.backgroundType = 'image';
      this.backgroundImage = new Image();
      this.backgroundImage.onload = () => {
        this.backgroundLoaded = true;
        console.log('RenderingSystem: Background image loaded successfully');
      };
      this.backgroundImage.onerror = () => {
        console.error(
          'RenderingSystem: Failed to load background image, falling back to default'
        );
        this.backgroundType = 'default';
        this.backgroundImage = undefined;
        this.backgroundLoaded = false;
      };
      this.backgroundImage.src = imageSrc;
      return;
    }

    // Default fallback
    console.log(
      'RenderingSystem: Invalid background source, using default black'
    );
    this.backgroundType = 'default';
  }

  /**
   * Create a programmatic desert background
   */
  private createDesertBackground(): void {
    // Create a canvas element to draw the desert background
    const bgCanvas = document.createElement('canvas');
    bgCanvas.width = this.canvas.width;
    bgCanvas.height = this.canvas.height;
    const bgContext = bgCanvas.getContext('2d')!;

    // Create desert gradient background
    const gradient = bgContext.createLinearGradient(0, 0, 0, bgCanvas.height);
    gradient.addColorStop(0, '#F4E4BC'); // Light sand at top
    gradient.addColorStop(0.3, '#D2B48C'); // Medium sand
    gradient.addColorStop(0.7, '#CD853F'); // Darker sand
    gradient.addColorStop(1, '#8B4513'); // Dark brown at bottom

    bgContext.fillStyle = gradient;
    bgContext.fillRect(0, 0, bgCanvas.width, bgCanvas.height);

    // Add sand dunes
    for (let i = 0; i < 8; i++) {
      const x = (i * bgCanvas.width) / 8;
      const y = bgCanvas.height - 50 - Math.sin(i * 0.5) * 30;

      bgContext.beginPath();
      bgContext.moveTo(x - 100, bgCanvas.height);
      bgContext.quadraticCurveTo(x, y, x + 100, bgCanvas.height);
      bgContext.fillStyle = '#DEB887';
      bgContext.fill();
    }

    // Add sand particles
    for (let i = 0; i < 200; i++) {
      const x = Math.random() * bgCanvas.width;
      const y = Math.random() * bgCanvas.height;
      const size = Math.random() * 2 + 1;

      bgContext.beginPath();
      bgContext.arc(x, y, size, 0, Math.PI * 2);
      bgContext.fillStyle = `rgba(255, 215, 0, ${Math.random() * 0.3})`;
      bgContext.fill();
    }

    // Add heat shimmer effect
    for (let i = 0; i < 20; i++) {
      const x = Math.random() * bgCanvas.width;
      const y = Math.random() * bgCanvas.height * 0.3;

      bgContext.beginPath();
      bgContext.moveTo(x, y);
      bgContext.lineTo(x + Math.random() * 20 - 10, y + Math.random() * 10);
      bgContext.strokeStyle = `rgba(255, 255, 255, ${Math.random() * 0.1})`;
      bgContext.lineWidth = 1;
      bgContext.stroke();
    }

    // Convert canvas to image
    this.backgroundImage = new Image();
    this.backgroundImage.onload = () => {
      this.backgroundLoaded = true;
      console.log('RenderingSystem: Desert background created successfully');
    };
    this.backgroundImage.src = bgCanvas.toDataURL();
  }

  /**
   * Render background based on type
   */
  private renderBackground(): void {
    switch (this.backgroundType) {
      case 'image':
        if (this.backgroundImage && this.backgroundLoaded) {
          // Render background image to fill the entire canvas
          this.context.drawImage(
            this.backgroundImage,
            0,
            0,
            this.canvas.width,
            this.canvas.height
          );
        } else {
          // Image failed to load, fallback to default
          this.context.fillStyle = '#000000';
          this.context.fillRect(0, 0, this.canvas.width, this.canvas.height);
        }
        break;

      case 'color':
        if (this.backgroundColor) {
          // Render solid color background
          this.context.fillStyle = this.backgroundColor;
          this.context.fillRect(0, 0, this.canvas.width, this.canvas.height);
        } else {
          // Fallback to default
          this.context.fillStyle = '#000000';
          this.context.fillRect(0, 0, this.canvas.width, this.canvas.height);
        }
        break;

      case 'transparent':
        // Don't fill anything - let PacmanGameWrapper background show through
        return;

      case 'default':
      default:
        // Default black background
        this.context.fillStyle = '#000000';
        this.context.fillRect(0, 0, this.canvas.width, this.canvas.height);
        break;
    }
  }

  /**
   * Render a circle
   */
  private renderCircle(
    sprite: SpriteComponent,
    width: number,
    height: number
  ): void {
    const radius = Math.min(width, height) / 2;
    this.context.fillStyle = sprite.color;
    this.context.beginPath();
    this.context.arc(0, 0, radius, 0, 2 * Math.PI);
    this.context.fill();
  }

  /**
   * Render Pacman with mouth direction
   */
  private renderPacman(
    sprite: SpriteComponent,
    width: number,
    height: number
  ): void {
    const radius = width / 2;
    const direction = sprite.renderingData?.direction || 'right';
    const isEating = sprite.renderingData?.isEating || false;
    const eatingTimer = sprite.renderingData?.eatingTimer || 0;
    const isSuperMode = sprite.renderingData?.isSuperMode || false;

    // Super mode multi-color blinking effect
    if (isSuperMode) {
      const colors = ['#FFD700', '#FFA500', '#FF6347', '#00FF00', '#FF00FF'];
      const colorIndex = Math.floor(Date.now() / 100) % colors.length;
      this.context.fillStyle = colors[colorIndex];
    } else {
      this.context.fillStyle = sprite.color;
    }

    this.context.beginPath();

    // If eating, show closed mouth (full circle)
    if (isEating && eatingTimer > 0) {
      this.context.arc(0, 0, radius, 0, 2 * Math.PI);
    } else {
      // Normal mouth direction
      let startAngle = 0;
      let endAngle = 2 * Math.PI;

      switch (direction) {
        case 'right':
          startAngle = 0.2 * Math.PI;
          endAngle = 1.8 * Math.PI;
          break;
        case 'left':
          startAngle = 1.2 * Math.PI;
          endAngle = 0.8 * Math.PI;
          break;
        case 'up':
          startAngle = 1.7 * Math.PI;
          endAngle = 1.3 * Math.PI;
          break;
        case 'down':
          startAngle = 0.7 * Math.PI;
          endAngle = 0.3 * Math.PI;
          break;
      }

      this.context.arc(0, 0, radius, startAngle, endAngle);
      this.context.lineTo(0, 0);
    }

    this.context.fill();

    // Add super mode glow effect
    if (isSuperMode) {
      this.context.save();
      this.context.globalAlpha = 0.3;
      this.context.shadowColor = '#FFD700';
      this.context.shadowBlur = 15;
      this.context.fillStyle = '#FFD700';
      this.context.beginPath();
      this.context.arc(0, 0, radius + 5, 0, Math.PI * 2);
      this.context.fill();
      this.context.restore();
    }
  }

  /**
   * Render a top-down person view - head from above with animated walking legs
   */
  private renderTopDownPerson(
    sprite: SpriteComponent,
    width: number,
    height: number
  ): void {
    const radius = width / 2;
    const direction = sprite.renderingData?.direction || 'right';
    const isMoving = sprite.renderingData?.isMoving || false;
    const isSuperMode = sprite.renderingData?.isSuperMode || false;

    // Rotate character to face movement direction
    // Character is drawn with head at (0, -radius) which is "up" in screen coordinates
    // We need to rotate so the head points in the movement direction
    let rotationAngle = 0;
    switch (direction) {
      case 'up':
        rotationAngle = 0; // Head already points up, no rotation needed
        break;
      case 'down':
        rotationAngle = Math.PI; // Rotate 180 degrees (head points down)
        break;
      case 'right':
        rotationAngle = Math.PI / 2; // Rotate 90 degrees clockwise (head points right)
        break;
      case 'left':
        rotationAngle = -Math.PI / 2; // Rotate 90 degrees counter-clockwise (head points left)
        break;
    }

    // Apply rotation
    this.context.save();
    this.context.rotate(rotationAngle);

    // Walking animation cycle based on time (reversed direction)
    const walkCycle = -Math.sin(Date.now() / 150) * (isMoving ? 1 : 0); // Reversed: negative sine for opposite leg movement

    // Head (seen from above - circular) - positioned at the BACK
    if (isSuperMode) {
      const colors = ['#FFD700', '#FFA500', '#FF6347', '#00FF00', '#FF00FF'];
      const colorIndex = Math.floor(Date.now() / 100) % colors.length;
      this.context.fillStyle = colors[colorIndex];
    } else {
      this.context.fillStyle = sprite.color || '#FFFF00'; // Head color
    }

    // Head at the back (opposite to movement direction)
    this.context.beginPath();
    this.context.arc(0, radius * 0.3, radius * 0.4, 0, Math.PI * 2);
    this.context.fill();

    // Add a small highlight on head for 3D effect
    this.context.fillStyle = 'rgba(255, 255, 255, 0.3)';
    this.context.beginPath();
    this.context.arc(
      -radius * 0.1,
      radius * 0.4,
      radius * 0.15,
      0,
      Math.PI * 2
    );
    this.context.fill();

    // Body/torso (seen from above - oval/rounded rectangle)
    const bodyColor = isSuperMode ? '#FFD700' : '#4169E1'; // Body color (blue shirt, gold when super)
    this.context.fillStyle = bodyColor;
    this.context.beginPath();
    this.context.ellipse(
      0,
      -radius * 0.1,
      radius * 0.35,
      radius * 0.5,
      0,
      0,
      Math.PI * 2
    );
    this.context.fill();

    // Legs - positioned at the FRONT (in movement direction) with walking animation
    const legRadius = radius * 0.15;
    const legSpread = radius * 0.4; // Distance from center
    const walkOffset = walkCycle * radius * 0.15; // Walking animation offset

    // Determine leg positions based on direction - legs at front (negative Y for base orientation)
    let leftLegX = 0,
      leftLegY = 0;
    let rightLegX = 0,
      rightLegY = 0;

    switch (direction) {
      case 'right':
        // Moving right - legs step forward/backward (not spreading sideways)
        leftLegX = -legSpread;
        leftLegY = -radius * 0.5 + (isMoving ? walkOffset : 0);
        rightLegX = legSpread;
        rightLegY = -radius * 0.5 - (isMoving ? walkOffset : 0);
        break;
      case 'left':
        // Moving left - legs step forward/backward (not spreading sideways)
        leftLegX = -legSpread;
        leftLegY = -radius * 0.5 - (isMoving ? walkOffset : 0);
        rightLegX = legSpread;
        rightLegY = -radius * 0.5 + (isMoving ? walkOffset : 0);
        break;
      case 'up':
        // Moving up - legs spread vertically at front (perfect - don't change)
        leftLegX = -legSpread;
        leftLegY = -radius * 0.5 + (isMoving ? walkOffset : 0);
        rightLegX = legSpread;
        rightLegY = -radius * 0.5 - (isMoving ? walkOffset : 0);
        break;
      case 'down':
        // Moving down - legs spread vertically at front (perfect - don't change)
        leftLegX = -legSpread;
        leftLegY = -radius * 0.5 - (isMoving ? walkOffset : 0);
        rightLegX = legSpread;
        rightLegY = -radius * 0.5 + (isMoving ? walkOffset : 0);
        break;
    }

    // Draw legs (circles seen from above - like footprints)
    this.context.fillStyle = '#8B4513'; // Brown/leg color

    // Left leg
    this.context.beginPath();
    this.context.arc(leftLegX, leftLegY, legRadius, 0, Math.PI * 2);
    this.context.fill();

    // Right leg
    this.context.beginPath();
    this.context.arc(rightLegX, rightLegY, legRadius, 0, Math.PI * 2);
    this.context.fill();

    // Add super mode glow effect
    if (isSuperMode) {
      this.context.save();
      this.context.globalAlpha = 0.3;
      this.context.shadowColor = '#FFD700';
      this.context.shadowBlur = 15;
      this.context.fillStyle = '#FFD700';
      this.context.beginPath();
      this.context.arc(0, 0, radius + 5, 0, Math.PI * 2);
      this.context.fill();
      this.context.restore();
    }

    // Restore rotation
    this.context.restore();
  }

  /**
   * Render a ghost with spinning fire/energy effect (non-directional, dangerous look)
   */
  private renderGhost(
    sprite: SpriteComponent,
    width: number,
    height: number
  ): void {
    const radius = width / 2;
    const centerX = 0;
    const centerY = 0;

    // Check if frightened (blue) or use ghost's original color
    const isFrightened = sprite.renderingData?.isFrightened || false;
    const baseColor = isFrightened ? '#2121FF' : sprite.color;

    // Create spinning fire/energy effect that looks dangerous from all angles
    // Spinning animation based on time
    const spinSpeed = 300; // milliseconds per rotation
    const rotation = ((Date.now() % spinSpeed) / spinSpeed) * Math.PI * 2;

    this.context.save();
    this.context.translate(centerX, centerY);
    this.context.rotate(rotation);

    // Draw spinning fire/energy effect with multiple flames/rays
    const flameCount = 8; // Number of flames/rays

    for (let i = 0; i < flameCount; i++) {
      const angle = (i / flameCount) * Math.PI * 2;
      const flameAngle = angle;

      // Create gradient for each flame
      const gradient = this.context.createRadialGradient(
        0,
        0,
        0,
        0,
        0,
        radius * 1.2
      );

      // Inner bright core
      const innerOpacity = isFrightened ? 0.9 : 1.0;
      const outerOpacity = isFrightened ? 0.3 : 0.5;

      gradient.addColorStop(0, this.hexToRgba(baseColor, innerOpacity));
      gradient.addColorStop(0.5, this.hexToRgba(baseColor, outerOpacity));
      gradient.addColorStop(1, this.hexToRgba(baseColor, 0));

      this.context.fillStyle = gradient;
      this.context.save();
      this.context.rotate(flameAngle);

      // Draw flame/ray as elongated ellipse
      this.context.beginPath();
      this.context.ellipse(0, 0, radius * 0.3, radius * 0.9, 0, 0, Math.PI * 2);
      this.context.fill();

      // Add inner bright core for intensity
      const coreGradient = this.context.createRadialGradient(
        0,
        0,
        0,
        0,
        0,
        radius * 0.4
      );
      coreGradient.addColorStop(0, this.hexToRgba(baseColor, 1));
      coreGradient.addColorStop(1, this.hexToRgba(baseColor, 0));
      this.context.fillStyle = coreGradient;
      this.context.beginPath();
      this.context.arc(0, 0, radius * 0.4, 0, Math.PI * 2);
      this.context.fill();

      this.context.restore();
    }

    // Draw central bright core
    const coreGradient = this.context.createRadialGradient(
      0,
      0,
      0,
      0,
      0,
      radius * 0.6
    );
    const coreInnerColor = isFrightened
      ? '#FFFFFF'
      : this.lightenColor(baseColor, 0.5);
    coreGradient.addColorStop(0, this.hexToRgba(coreInnerColor, 1));
    coreGradient.addColorStop(0.7, this.hexToRgba(baseColor, 0.8));
    coreGradient.addColorStop(1, this.hexToRgba(baseColor, 0));

    this.context.fillStyle = coreGradient;
    this.context.beginPath();
    this.context.arc(0, 0, radius * 0.6, 0, Math.PI * 2);
    this.context.fill();

    this.context.restore();
  }

  /**
   * Helper: Convert hex color to rgba string
   */
  private hexToRgba(hex: string, alpha: number): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  /**
   * Helper: Lighten a hex color
   */
  private lightenColor(hex: string, factor: number): string {
    const r = Math.min(255, parseInt(hex.slice(1, 3), 16) + 255 * factor);
    const g = Math.min(255, parseInt(hex.slice(3, 5), 16) + 255 * factor);
    const b = Math.min(255, parseInt(hex.slice(5, 7), 16) + 255 * factor);
    return `#${Math.floor(r).toString(16).padStart(2, '0')}${Math.floor(g)
      .toString(16)
      .padStart(2, '0')}${Math.floor(b).toString(16).padStart(2, '0')}`;
  }

  /**
   * Render power pellet as a glowing full circle dot
   */
  private renderPowerPellet(
    sprite: SpriteComponent,
    width: number,
    height: number
  ): void {
    const radius = width / 2;

    // Pulse animation for power pellet (glowing effect)
    const pulseSpeed = 800; // milliseconds per pulse cycle
    const pulsePhase = (Date.now() % pulseSpeed) / pulseSpeed;
    const pulseScale = 0.8 + Math.sin(pulsePhase * Math.PI * 2) * 0.2; // Scale between 0.8 and 1.0

    this.context.save();

    // Draw outer glow (larger, more transparent)
    const glowGradient = this.context.createRadialGradient(
      0,
      0,
      0,
      0,
      0,
      radius * 1.8 * pulseScale
    );
    glowGradient.addColorStop(0, this.hexToRgba(sprite.color, 0.4));
    glowGradient.addColorStop(0.5, this.hexToRgba(sprite.color, 0.2));
    glowGradient.addColorStop(1, this.hexToRgba(sprite.color, 0));

    this.context.fillStyle = glowGradient;
    this.context.beginPath();
    this.context.arc(0, 0, radius * 1.8 * pulseScale, 0, Math.PI * 2);
    this.context.fill();

    // Draw main circle with inner glow
    const mainGradient = this.context.createRadialGradient(
      0,
      0,
      0,
      0,
      0,
      radius * pulseScale
    );
    const brightColor = this.lightenColor(sprite.color, 0.3);
    mainGradient.addColorStop(0, this.hexToRgba(brightColor, 1));
    mainGradient.addColorStop(0.7, this.hexToRgba(sprite.color, 1));
    mainGradient.addColorStop(1, this.hexToRgba(sprite.color, 0.8));

    this.context.fillStyle = mainGradient;
    this.context.beginPath();
    this.context.arc(0, 0, radius * pulseScale, 0, Math.PI * 2);
    this.context.fill();

    // Add bright center highlight
    this.context.fillStyle = this.hexToRgba(brightColor, 0.9);
    this.context.beginPath();
    this.context.arc(0, 0, radius * 0.4 * pulseScale, 0, Math.PI * 2);
    this.context.fill();

    this.context.restore();
  }

  /**
   * Render custom shape using points array
   */
  private renderCustomShape(sprite: SpriteComponent): void {
    if (sprite.points.length < 3) {
      // Fallback to rectangle if not enough points
      this.renderRectangle(sprite, sprite.size.x, sprite.size.y);
      return;
    }

    this.context.fillStyle = sprite.color;
    this.context.beginPath();
    this.context.moveTo(sprite.points[0].x, sprite.points[0].y);

    for (let i = 1; i < sprite.points.length; i++) {
      this.context.lineTo(sprite.points[i].x, sprite.points[i].y);
    }

    this.context.closePath();
    this.context.fill();
  }

  /**
   * Render an animated sprite (when textures are available)
   */
  private renderAnimatedSprite(
    sprite: SpriteComponent,
    animation: AnimationComponent,
    entity: Entity
  ): void {
    // For now, fall back to shape rendering since we don't have actual textures
    // This can be enhanced later with actual sprite sheet rendering
    this.renderByType(sprite);

    // TODO: Add actual texture/sprite sheet rendering when assets are available
    // const currentFrame = animation.getCurrentFrame();
    // if (currentFrame && sprite.texture) {
    //   this.context.drawImage(sprite.texture, ...);
    // }
  }

  /**
   * Set debug rendering on/off
   */
  public setDebugRendering(enabled: boolean): void {
    // TODO: Add debug rendering flag and use it in renderEntity
  }
}
